package dev.lingvo.site.components.editor

import com.varabyte.kobweb.browser.dom.observers.ResizeObserver
import org.w3c.dom.Element

internal class EditorMeasurementController {
    private var observer: ResizeObserver? = null
    private var bindingToken = 0L
    private var bufferedId: Long? = null
    private var bufferedMeasurement: EditorWorkbenchMeasurement? = null

    var acceptedMeasurement: EditorAcceptedMeasurement? = null
        private set

    fun bind(
        measurementId: Long,
        workspace: Element?,
        editorScroller: Element?,
        onMeasurement: (Long, EditorWorkbenchMeasurement?) -> Unit,
        onStale: (Long, Long) -> Unit,
    ) {
        disconnect()
        if (workspace == null && editorScroller == null) return
        val token = ++bindingToken
        observer = ResizeObserver {
            val measurement = readEditorWorkbenchMeasurement()
            if (token != bindingToken) {
                onStale(measurementId, measurementId)
            } else {
                onMeasurement(measurementId, measurement)
            }
        }
        workspace?.let { observer?.observe(it) }
        editorScroller?.takeIf { it !== workspace }?.let { observer?.observe(it) }
    }

    fun disconnect() {
        bindingToken += 1
        observer?.disconnect()
        observer = null
        bufferedId = null
        bufferedMeasurement = null
    }

    fun buffer(measurementId: Long, measurement: EditorWorkbenchMeasurement?) {
        bufferedId = measurementId
        bufferedMeasurement = measurement
    }

    fun takeBuffered(
        measurementId: Long,
        evidence: EditorPostCommitEvidenceState,
        workspaceClientWidth: Double?,
    ): EditorWorkbenchMeasurement? {
        if (
            !evidence.permitsObserverResult(measurementId) ||
            bufferedId != measurementId ||
            workspaceClientWidth == null
        ) return null
        val measurement = bufferedMeasurement ?: return null
        bufferedId = null
        bufferedMeasurement = null
        return measurement
    }

    fun accept(
        measurementId: Long,
        measurement: EditorWorkbenchMeasurement,
        workspaceClientWidth: Double,
    ) {
        acceptedMeasurement = EditorAcceptedMeasurement(
            id = measurementId,
            measurement = measurement,
            workspaceClientWidth = workspaceClientWidth,
        )
    }

    fun invalidate() {
        acceptedMeasurement = null
        bufferedId = null
        bufferedMeasurement = null
    }

    fun readCurrentMeasurement(): EditorWorkbenchMeasurement? = readEditorWorkbenchMeasurement()
}

internal fun measurementHasExternalWidthChange(
    accepted: EditorAcceptedMeasurement,
    workspaceClientWidth: Double,
    editorClientWidth: Double,
): Boolean =
    kotlin.math.abs(accepted.workspaceClientWidth - workspaceClientWidth) > 1.0 ||
        kotlin.math.abs(accepted.measurement.editorScrollClientWidth - editorClientWidth) > 1.0

internal fun measurementHasExternalWorkspaceWidthChange(
    accepted: EditorAcceptedMeasurement,
    workspaceClientWidth: Double,
): Boolean = kotlin.math.abs(accepted.workspaceClientWidth - workspaceClientWidth) > 1.0

internal fun measurementNeedsTargetRemeasure(
    accepted: EditorAcceptedMeasurement,
    activeTargetWidth: Double?,
    editableTargetExpected: Boolean,
): Boolean = accepted.measurement.activeTargetWidth == null &&
    editableTargetExpected && activeTargetWidth != null
